import { getPreloadMargin } from '@theme/loading-scheduler';

const CACHE_KEY = 'frete_localizacao_v2';
const CACHE_TTL = 6 * 60 * 60 * 1000;

function cachedLocation() {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY));
    return cache?.time && Date.now() - cache.time < CACHE_TTL && cache.data ? cache.data : null;
  } catch {
    return null;
  }
}

function lookupLocation() {
  let lookup = window.avanteFreteLookup;
  if (lookup?.promise && Date.now() - lookup.time < CACHE_TTL) return lookup.promise;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 3000) : null;

  lookup = { time: Date.now(), promise: null };
  window.avanteFreteLookup = lookup;
  lookup.promise = fetch('https://ipwho.is/?fields=success,country_code,city,region_code', {
    cache: 'no-store',
    credentials: 'omit',
    signal: controller?.signal,
  })
    .then((response) => {
      if (!response.ok) throw new Error('Falha na geolocalização');
      return response.json();
    })
    .then((data) => {
      if (!data || data.success === false) throw new Error('Localização indisponível');
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), data }));
      } catch {
        // O texto padrão permanece visível quando o armazenamento não está disponível.
      }
      return data;
    })
    .catch(() => {
      if (window.avanteFreteLookup === lookup) delete window.avanteFreteLookup;
      return null;
    })
    .finally(() => {
      if (timer) clearTimeout(timer);
    });

  return lookup.promise;
}

class AvanteFreteCorreios extends HTMLElement {
  #local = null;
  #contentObserver = null;
  #visibilityObserver = null;
  #watchingProximity = false;

  #checkProximity = () => {
    const box = this.getBoundingClientRect();
    const margin = getPreloadMargin();
    if (box.top > window.innerHeight + margin || box.bottom < -margin) return;
    this.#stopWatchingProximity();
    this.#requestLocation();
  };

  #stopWatchingProximity() {
    if (!this.#watchingProximity) return;
    window.removeEventListener('scroll', this.#checkProximity);
    window.removeEventListener('resize', this.#checkProximity);
    this.#watchingProximity = false;
  }

  #requestLocation() {
    const currentCache = cachedLocation();
    if (currentCache) {
      this.#show(currentCache);
      return;
    }
    lookupLocation().then((data) => {
      if (this.isConnected) this.#show(data);
    });
  }

  connectedCallback() {
    this.#contentObserver = new MutationObserver(() => this.#syncLocation());
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    this.#syncLocation();
  }

  disconnectedCallback() {
    this.#contentObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#stopWatchingProximity();
    this.#contentObserver = null;
    this.#visibilityObserver = null;
    this.#local = null;
  }

  #show(data) {
    if (!data || data.country_code !== 'BR' || typeof data.city !== 'string' || !data.city) return;
    const location =
      data.city +
      (typeof data.region_code === 'string' && data.region_code ? ', ' + data.region_code : '') +
      ' e Região';
    if (this.#local && this.#local.textContent !== location) this.#local.textContent = location;
  }

  #syncLocation() {
    const local = this.querySelector('[data-local-frete]');
    if (!local) return;
    this.#local = local;

    const cached = cachedLocation();
    if (cached) {
      this.#visibilityObserver?.disconnect();
      this.#stopWatchingProximity();
      this.#show(cached);
      return;
    }

    if (!this.#visibilityObserver && 'IntersectionObserver' in window) {
      this.#visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#visibilityObserver?.unobserve(this);
          this.#requestLocation();
        },
        { rootMargin: `${getPreloadMargin()}px 0px` }
      );
    }
    if (this.#visibilityObserver) {
      this.#visibilityObserver.observe(this);
    } else {
      if (!this.#watchingProximity) {
        window.addEventListener('scroll', this.#checkProximity, { passive: true });
        window.addEventListener('resize', this.#checkProximity);
        this.#watchingProximity = true;
      }
      this.#checkProximity();
    }
  }
}

if (!customElements.get('avante-frete-correios')) {
  customElements.define('avante-frete-correios', AvanteFreteCorreios);
}
